import { EllipsisVerticalIcon, MapPinIcon } from "@heroicons/react/20/solid";
import { AVAILABILITY_TYPES, format24to12 } from "../../config/availability";
import { COLOUR } from "../../config/constants";
import { useUserPreferences } from "../../hooks/useUserPreferences";
import StatusIndicator from "../Shared/StatusIndicator";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { Fragment, useState } from "react";

// Whether a row is an on-site (fixed-location) availability. Everything else
// (distance / region) is a "Mobile" delivery type. Used to pick the per-row
// Type badge and the "Where" cell (coverage vs address) in the unified list.
const isFixed = (availability) =>
  availability.availabilityType === AVAILABILITY_TYPES.FIXED;

// Small delivery-type pill so a mixed (unfiltered) list is scannable at a
// glance: "Mobile" (provider travels) vs "On-site" (clients come to them).
const TypeBadge = ({ availability }) => (
  <span className="inline-flex items-center rounded-pill bg-cream-100 px-2 py-0.5 text-xs font-medium text-ink-600">
    {isFixed(availability) ? "On-site" : "Mobile"}
  </span>
);

const AvailabilityDataTable = ({
  availabilities,
  patchAvailability,
  deleteAvailability,
  editAvailability,
}) => {
  const { is12HourClock } = useUserPreferences();
  const [expandedRegions, setExpandedRegions] = useState({});
  const [expandedSchedules, setExpandedSchedules] = useState({});

  const handleActiveStatusClick = (availability) => {
    const formData = new FormData();
    formData.append("isActive", !availability.isActive);
    patchAvailability({ id: availability._id, formData });
  };

  const toggleRegionsExpansion = (availabilityId) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [availabilityId]: !prev[availabilityId],
    }));
  };

  const toggleScheduleExpansion = (availabilityId) => {
    setExpandedSchedules((prev) => ({
      ...prev,
      [availabilityId]: !prev[availabilityId],
    }));
  };

  const formatTime = (time) => {
    if (is12HourClock) {
      return format24to12(time);
    } else {
      return time;
    }
  };

  const formatWeeklyAvailability = (weeklyAvailability, availabilityId) => {
    if (!weeklyAvailability || Object.keys(weeklyAvailability).length === 0) {
      return "No availability set";
    }

    const availableDays = Object.entries(weeklyAvailability).filter(
      ([day, times]) => times && times.length > 0
    );

    if (availableDays.length === 0) {
      return "No availability set";
    }

    const isExpanded = expandedSchedules[availabilityId];

    // Format all days first
    const allFormattedDays = availableDays.map(([day, times]) => {
      const shortDay =
        day.slice(0, 3).charAt(0).toUpperCase() + day.slice(1, 3);

      // Format time blocks
      const timeBlocks = times.map(
        (time) => `${formatTime(time.startTime)}-${formatTime(time.endTime)}`
      );

      return {
        day: shortDay,
        timeBlocks: timeBlocks,
      };
    });

    // Determine if there's more content to show
    const firstDay = allFormattedDays[0];
    const hasMoreDays = availableDays.length > 1;
    const hasMoreTimeBlocks = firstDay.timeBlocks.length > 1;
    const hasMore = hasMoreDays || hasMoreTimeBlocks;

    return {
      display: allFormattedDays[0], // Only show the first day
      hasMore,
      isExpanded,
      allDays: allFormattedDays,
    };
  };

  const formatRegions = (regions, availabilityId) => {
    if (!regions || regions.length === 0)
      return {
        display: "No regions",
        hasMore: false,
        isExpanded: false,
        allRegions: [],
      };

    const isExpanded = expandedRegions[availabilityId];

    if (regions.length === 1) {
      return {
        display: regions[0].name,
        hasMore: false,
        isExpanded: false,
        allRegions: [regions[0].name],
      };
    }

    return {
      display: regions[0].name,
      hasMore: regions.length > 1,
      isExpanded,
      allRegions: regions.map((region) => region.name),
    };
  };

  // Short one-line coverage summary for the Mobile tab's distance rows.
  // Region rows render the existing expandable region list instead.
  const formatDistanceCoverage = (availability) => {
    const base = availability.baseAddress?.address;
    const within =
      availability.distance != null
        ? `Within ${availability.distance} ${availability.distanceUnit || ""}`.trim()
        : "Travel radius not set";
    return base ? `${within} of ${base}` : within;
  };

  return (
    <div className="mb-8">

      {/* Mobile view - Card layout */}
      <div className="sm:hidden">
        <div className="space-y-4">
          {availabilities.map((availability) => {
            const regionsData = formatRegions(
              availability.regions,
              availability._id
            );
            const scheduleData = formatWeeklyAvailability(
              availability.weeklyAvailability,
              availability._id
            );

            return (
              <div
                key={availability._id}
                className="bg-white rounded-lg border border-ink-100 p-4"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-ink-900 truncate">
                      {availability.name}
                    </div>
                    <div className="mt-1">
                      <TypeBadge availability={availability} />
                    </div>
                  </div>
                  <Menu as="div" className="relative flex-none ml-4">
                    <MenuButton className="-m-2.5 block p-2.5 text-ink-500 hover:text-ink-900">
                      <span className="sr-only">Open options</span>
                      <EllipsisVerticalIcon
                        aria-hidden="true"
                        className="size-5"
                      />
                    </MenuButton>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems anchor="bottom end" className="z-[9999] w-32 rounded-md bg-white py-2 shadow-lg ring-1 ring-ink-100 focus:outline-none">
                        <MenuItem>
                          {({ close }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                editAvailability(availability._id);
                                close();
                              }}
                              className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                            >
                              Edit
                              <span className="sr-only">
                                , {availability.name}
                              </span>
                            </a>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ close }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                handleActiveStatusClick(availability);
                                close();
                              }}
                              className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                            >
                              {availability.isActive ? "Disable" : "Enable"}
                              <span className="sr-only">
                                ,{" "}
                                {availability.isActive ? "Disable" : "Enable"}{" "}
                                {availability.name}
                              </span>
                            </a>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ close }) => (
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                deleteAvailability(availability._id);
                                close();
                              }}
                              className="block px-3 py-1 text-sm/6 text-signal-red-600 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                            >
                              Delete
                              <span className="sr-only">
                                , {availability.name}
                              </span>
                            </a>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>

                <div className="mt-4 space-y-3">
                  {availability.availabilityType ===
                    AVAILABILITY_TYPES.REGION && (
                      <div className="flex items-start">
                        <span className="text-sm text-ink-500 w-20">
                          Coverage:
                        </span>
                        <div className="ml-2 flex-1">
                          <div className="space-y-1">
                            {(regionsData.isExpanded
                              ? regionsData.allRegions
                              : regionsData.allRegions.slice(0, 1)
                            ).map((region, index) => (
                              <div
                                key={index}
                                className="text-sm text-ink-700 flex items-center"
                              >
                                <MapPinIcon className="w-4 h-4 text-ink-400 mr-2 flex-shrink-0" />
                                {region}
                              </div>
                            ))}
                          </div>
                          {regionsData.hasMore && (
                            <div className="mt-2">
                              <button
                                onClick={() =>
                                  toggleRegionsExpansion(availability._id)
                                }
                                className="text-sm font-medium text-accent-600 hover:text-accent-500"
                              >
                                {regionsData.isExpanded
                                  ? "Show less"
                                  : "Show more"}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  {availability.availabilityType ===
                    AVAILABILITY_TYPES.DISTANCE && (
                      <div className="flex items-start">
                        <span className="text-sm text-ink-500 w-20">
                          Coverage:
                        </span>
                        <span className="text-sm text-ink-900 ml-2 flex-1">
                          {formatDistanceCoverage(availability)}
                        </span>
                      </div>
                    )}
                  {isFixed(availability) && (
                    <div className="flex items-center">
                      <span className="text-sm text-ink-500 w-20">
                        Address:
                      </span>
                      <span className="text-sm text-ink-900 ml-2 truncate">
                        {availability.baseAddress?.address || "No address"}
                      </span>
                    </div>
                  )}
                  <div className="flex items-start">
                    <span className="text-sm text-ink-500 w-20">
                      Schedule:
                    </span>
                    <div className="ml-2 flex-1">
                      <div className="text-sm text-ink-900">
                        {typeof scheduleData === 'string' ? (
                          scheduleData
                        ) : (
                          <div className="flex">
                            <span className="font-medium inline-block w-10">
                              {scheduleData.display.day}:
                            </span>
                            <span className="ml-2 text-sm">
                              {scheduleData.display.timeBlocks[0]}
                            </span>
                          </div>
                        )}
                      </div>
                      {scheduleData.hasMore && !scheduleData.isExpanded && (
                        <div className="mt-1">
                          <button
                            onClick={() =>
                              toggleScheduleExpansion(availability._id)
                            }
                            className="text-sm font-medium text-accent-600 hover:text-accent-500"
                          >
                            Show more
                          </button>
                        </div>
                      )}
                      {scheduleData.isExpanded && (
                          <div className="mt-2 space-y-2">
                            {/* Show remaining time blocks for first day */}
                            {scheduleData.display.timeBlocks
                              .slice(1)
                              .map((timeBlock, index) => (
                                <div key={index} className="flex">
                                  <span className="inline-block w-10"></span>
                                  <span className="ml-2 text-sm text-ink-700">
                                    {timeBlock}
                                  </span>
                                </div>
                              ))}

                            {/* Show all other days with all their time blocks */}
                            {scheduleData.allDays
                              .slice(1)
                              .map((day, index) => (
                                <div key={index}>
                                  <div className="flex">
                                    <span className="font-medium text-ink-900 inline-block w-10">
                                      {day.day}:
                                    </span>
                                    <span className="ml-2 text-sm text-ink-900">
                                      {day.timeBlocks[0]}
                                    </span>
                                  </div>
                                  {day.timeBlocks
                                    .slice(1)
                                    .map((timeBlock, timeIndex) => (
                                      <div key={timeIndex} className="flex">
                                        <span className="inline-block w-10"></span>
                                        <span className="ml-2 text-sm text-ink-700">
                                          {timeBlock}
                                        </span>
                                      </div>
                                    ))}
                                </div>
                              ))}
                            <div className="mt-2">
                              <button
                                onClick={() =>
                                  toggleScheduleExpansion(availability._id)
                                }
                                className="text-sm font-medium text-accent-600 hover:text-accent-500"
                              >
                                Show less
                              </button>
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-ink-500 w-20">
                      Status:
                    </span>
                    <div className="ml-2">
                      <StatusIndicator
                        colour={
                          availability.isActive ? COLOUR.GREEN : COLOUR.GRAY
                        }
                        label={
                          availability.isActive ? "Active" : "Inactive"
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop view - Table layout */}
      <div className="hidden sm:block">
        <div className="overflow-visible bg-white border border-ink-100 shadow-hairline sm:rounded-card">
          <table className="min-w-full">
            <thead className="border-b border-ink-100">
              <tr>
                <th
                  scope="col"
                  className="py-3 pl-4 pr-3 text-left smallcaps text-ink-400 sm:pl-6"
                >
                  Name
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left smallcaps text-ink-400"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left smallcaps text-ink-400"
                >
                  Where
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left smallcaps text-ink-400"
                >
                  Schedule
                </th>
                <th
                  scope="col"
                  className="px-3 py-3 text-left smallcaps text-ink-400"
                >
                  Status
                </th>
                <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100 bg-white">
              {availabilities.map((availability) => {
                const regionsData = formatRegions(
                  availability.regions,
                  availability._id
                );
                const scheduleData = formatWeeklyAvailability(
                  availability.weeklyAvailability,
                  availability._id
                );

                return (
                  <tr key={availability._id}>
                    <td className="w-full max-w-0 py-4 pl-4 pr-3 text-sm font-medium text-ink-900 sm:w-auto sm:max-w-none sm:pl-6">
                      {availability.name}
                    </td>
                    <td className="px-3 py-4 text-sm text-ink-500">
                      <TypeBadge availability={availability} />
                    </td>
                    <td className="px-3 py-4 text-sm text-ink-500 max-w-xs">
                      {isFixed(availability) ? (
                        <span className="text-ink-700 truncate block">
                          {availability.baseAddress?.address || "No address"}
                        </span>
                      ) : availability.availabilityType ===
                        AVAILABILITY_TYPES.REGION ? (
                        <div>
                          <div className="space-y-1">
                            {(regionsData.isExpanded
                              ? regionsData.allRegions
                              : regionsData.allRegions.slice(0, 1)
                            ).map((region, index) => (
                              <div
                                key={index}
                                className="text-sm text-ink-700 flex items-center"
                              >
                                <MapPinIcon className="w-4 h-4 text-ink-400 mr-2 flex-shrink-0" />
                                {region}
                              </div>
                            ))}
                          </div>
                          {regionsData.hasMore && (
                            <div className="mt-2">
                              <button
                                onClick={() =>
                                  toggleRegionsExpansion(availability._id)
                                }
                                className="text-sm font-medium text-accent-600 hover:text-accent-500"
                              >
                                {regionsData.isExpanded
                                  ? "Show less"
                                  : "Show more"}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-ink-700">
                          {formatDistanceCoverage(availability)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-ink-500 max-w-xs">
                      <div>
                        <div className="text-sm text-ink-900">
                          {typeof scheduleData === 'string' ? (
                            scheduleData
                          ) : (
                              <div className="flex">
                              <span className="font-medium inline-block w-10">
                                {scheduleData.display.day}:
                              </span>
                              <span className="ml-2 text-sm">
                                {scheduleData.display.timeBlocks[0]}
                              </span>
                            </div>
                          )}
                        </div>
                        {scheduleData.hasMore && !scheduleData.isExpanded && (
                          <div className="mt-1">
                            <button
                              onClick={() =>
                                toggleScheduleExpansion(availability._id)
                              }
                              className="text-sm font-medium text-accent-600 hover:text-accent-500"
                            >
                              Show more
                            </button>
                          </div>
                        )}
                        {scheduleData.isExpanded && (
                            <div className="mt-2 space-y-2">
                              {/* Show remaining time blocks for first day */}
                              {scheduleData.display.timeBlocks
                                .slice(1)
                                .map((timeBlock, index) => (
                                  <div key={index} className="flex">
                                    <span className="inline-block w-10"></span>
                                    <span className="ml-2 text-sm text-ink-700">
                                      {timeBlock}
                                    </span>
                                  </div>
                                ))}

                              {/* Show all other days with all their time blocks */}
                              {scheduleData.allDays
                                .slice(1)
                                .map((day, index) => (
                                  <div key={index}>
                                    <div className="flex">
                                      <span className="font-medium text-ink-900 inline-block w-10">
                                        {day.day}:
                                      </span>
                                      <span className="ml-2 text-sm text-ink-900">
                                        {day.timeBlocks[0]}
                                      </span>
                                    </div>
                                    {day.timeBlocks
                                      .slice(1)
                                      .map((timeBlock, timeIndex) => (
                                        <div key={timeIndex} className="flex">
                                          <span className="inline-block w-10"></span>
                                          <span className="ml-2 text-sm text-ink-700">
                                            {timeBlock}
                                          </span>
                                        </div>
                                      ))}
                                  </div>
                                ))}
                              <div className="mt-2">
                                <button
                                  onClick={() =>
                                    toggleScheduleExpansion(availability._id)
                                  }
                                  className="text-sm font-medium text-accent-600 hover:text-accent-500"
                                >
                                  Show less
                                </button>
                              </div>
                            </div>
                          )}
                      </div>
                    </td>
                    <td className="px-3 py-4 text-sm text-ink-500">
                      <StatusIndicator
                        colour={
                          availability.isActive ? COLOUR.GREEN : COLOUR.GRAY
                        }
                        label={
                          availability.isActive ? "Active" : "Inactive"
                        }
                      />
                    </td>
                    <td className="py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <Menu as="div" className="relative flex-none">
                        <MenuButton className="-m-2.5 block p-2.5 text-ink-500 hover:text-ink-900">
                          <span className="sr-only">Open options</span>
                          <EllipsisVerticalIcon
                            aria-hidden="true"
                            className="size-5"
                          />
                        </MenuButton>

                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-100"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-75"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <MenuItems anchor="bottom end" className="z-[9999] w-32 rounded-md bg-white py-2 shadow-lg ring-1 ring-ink-100 focus:outline-none">
                            <MenuItem>
                              {({ close }) => (
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    editAvailability(availability._id);
                                    close();
                                  }}
                                  className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                >
                                  Edit
                                  <span className="sr-only">
                                    , {availability.name}
                                  </span>
                                </a>
                              )}
                            </MenuItem>
                            <MenuItem>
                              {({ close }) => (
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleActiveStatusClick(availability);
                                    close();
                                  }}
                                  className="block px-3 py-1 text-sm/6 text-ink-900 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                >
                                  {availability.isActive
                                    ? "Disable"
                                    : "Enable"}
                                  <span className="sr-only">
                                    ,{" "}
                                    {availability.isActive
                                      ? "Disable"
                                      : "Enable"}{" "}
                                    {availability.name}
                                  </span>
                                </a>
                              )}
                            </MenuItem>
                            <MenuItem>
                              {({ close }) => (
                                <a
                                  href="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    deleteAvailability(availability._id);
                                    close();
                                  }}
                                  className="block px-3 py-1 text-sm/6 text-signal-red-600 text-left data-[focus]:bg-cream-100 data-[focus]:outline-none"
                                >
                                  Delete
                                  <span className="sr-only">
                                    , {availability.name}
                                  </span>
                                </a>
                              )}
                            </MenuItem>
                          </MenuItems>
                        </Transition>
                      </Menu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityDataTable;